import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { partnerApi } from "../../../../../../../../services/Api/Partner/partnerApi";
import {
	PartnerApi,
	PartnerUserListApi,
	UserPermissions,
} from "../../../../../../../../services/Api/Partner/partnerTypes";
import { OrganizationApi } from "../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { selectPartner } from "../../../../../../../../services/partner/selectors";
import AddPartnerUserModal from "../../components/AddPartnerUserModal";
import { ChangePermissionsModal } from "../../components/ChangePermissionsModal";

interface ListProps {
	resource?: Record<string, any>;
	partner: PartnerApi | null;
}

const listName = "PARTNER_USER";
const resourceType = "USER";

const List = ({ resource, partner }: ListProps) => {
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const segmentContextValue = React.useContext(SegmentContext);
	const [items, setItems] = useState<PartnerUserListApi[]>([]);
	const [params, setParams] = useState<Record<string, any>>({});
	const [showUserModal, setShowUserModal] = useState(false);
	const [changePermissionsModalUserEmail, setChangePermissionsModalUserEmail] = useState<string>();
	const isMobile = useWindowSize().isMobile;
	const [changePermissionsModalUserPermissions, setChangePermissionsModalUserPermissions] =
		useState<UserPermissions[]>();

	if (!resource || !partner) return null;
	const data = resource.read();

	const fetchItemsData = async () => {
		try {
			const res = await partnerApi.getPartnerUsers(Number(partner.id), params);
			setItems(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onHideUserModal = async (data?: OrganizationApi) => {
		setShowUserModal(false);
		if (data) {
			await fetchItemsData();
		}
	};

	const onClosePermissionsModal = () => {
		setChangePermissionsModalUserEmail(undefined);
		setChangePermissionsModalUserPermissions(undefined);
	};

	const onRemoveUser = async (item: PartnerUserListApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await partnerApi.removeUserFromPartner(Number(partner.id), item.email);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
			},
			{
				id: "email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "permissions",
				name: t("modules.user.field.permissions.title"),
				render: (item: PartnerUserListApi) => {
					return `${item.permissions
						.map((permission) => {
							switch (permission) {
								case UserPermissions.BASIC:
									return t("enums.user.permission.BASIC");
								case UserPermissions.USERS:
									return t("enums.user.permission.USERS");
								case UserPermissions.MONEY_FLOW:
									return t("enums.user.permission.MONEY_FLOW");
								default:
									return "";
							}
						})
						.join(", ")}`;
				},
			},
		],
		actions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: PartnerUserListApi) => onRemoveUser(item),
				refreshListAfter: true,
			},
			{
				name: t("modules.user.action.change_permissions.title"),
				click: (item: PartnerUserListApi) => {
					setChangePermissionsModalUserEmail(item.email);
					setChangePermissionsModalUserPermissions(item.permissions);
				},
			},
		],
		selectedColumns: ["name", "email", "permissions"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all_male"),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "organization";
			setParams(params);
			return partnerApi.getPartnerUsers(Number(partner.id), params, { cancelToken: sourceToken?.token });
		},
		exportConfig: {
			title: t("common.word.users", { ns: "lib" }),
			filename: t("common.word.users", { ns: "lib" }),
			organization: organization.name,
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	const buttons: ButtonProps[] = [
		{
			title: `+ ${t("common.action.add", { ns: "lib" })}`,
			variant: "primary",
			action: () => setShowUserModal(true),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: `+ ${t("common.action.add", { ns: "lib" })}`,
			action: () => setShowUserModal(true),
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("lib:common.word.users")} buttons={buttons} />}
			<ListData
				data={items}
				config={config}
				onFetch={(items: PartnerUserListApi[]) => setItems(items)}
				mobileActions={mobileActions}
			/>
			{showUserModal && <AddPartnerUserModal partnerId={Number(partner.id)} onHide={onHideUserModal} />}
			{changePermissionsModalUserEmail && (
				<ChangePermissionsModal
					partnerId={Number(partner.id)}
					userEmail={changePermissionsModalUserEmail}
					currentPermissions={changePermissionsModalUserPermissions || []}
					onClose={onClosePermissionsModal}
					refetchData={fetchItemsData}
				/>
			)}
		</>
	);
};

const PartnerPreviewUsersPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const partner = useSelector(selectPartner);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleChangeTabTitle(`${t("lib:common.word.users")} | ${t("modules.partner.field.partner_panel.title")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List partner={partner} resource={resource} />
		</Suspense>
	);
};
export default PartnerPreviewUsersPage;
